// src/components/admin/pos/sell/post-sale-training-dialog.tsx
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Calendar, Dumbbell, ShieldCheck, Wallet, Printer } from "lucide-react";

interface PostSaleTrainingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageName: string;
  memberName: string;
  totalCredits?: number;
  creditsPerSession?: number;
  slotDurationHours?: number;
  onCreateClass: () => void;
  onViewMemberTraining?: () => void;
  onPrintReceipt?: () => void;
  onClose: () => void;
}

export const PostSaleTrainingDialog: React.FC<PostSaleTrainingDialogProps> = ({
  open,
  onOpenChange,
  packageName,
  memberName,
  totalCredits = 10,
  creditsPerSession = 1,
  slotDurationHours = 1.0,
  onCreateClass,
  onViewMemberTraining,
  onPrintReceipt,
  onClose,
}) => {
  const sessionsCount = Math.floor(totalCredits / (creditsPerSession || 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-center sm:text-center pb-2">
          <div className="mx-auto size-12 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
            <CheckCircle2 className="size-7 text-emerald-600" />
          </div>
          <DialogTitle className="text-lg font-bold text-center">
            {packageName} sold to {memberName}
          </DialogTitle>
          <DialogDescription className="text-center text-xs">
            POS transaction completed and training package activated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-xs">
          {/* Main confirmation card */}
          <div className="p-3.5 bg-muted/40 rounded-lg border space-y-2">
            <div className="flex items-start gap-2.5">
              <Dumbbell className="size-4 text-primary shrink-0 mt-0.5" />
              <p className="text-foreground leading-relaxed">
                Based on <strong>{totalCredits} credits</strong> and the facility&apos;s minimum required credit per session (<strong>{creditsPerSession}</strong>), <strong>{sessionsCount} sessions</strong> are created automatically and shown on the coach&apos;s training screen, and the coach is notified.
              </p>
            </div>

            <div className="flex items-center gap-2 text-emerald-800 bg-emerald-50 p-2 rounded border border-emerald-200">
              <Wallet className="size-3.5 text-emerald-600 shrink-0" />
              <span>Credits are instantly added to the member&apos;s wallet.</span>
            </div>
          </div>

          {/* Business rules callout */}
          <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
            <div className="p-2 rounded border bg-white flex items-center gap-1.5">
              <ShieldCheck className="size-3.5 text-blue-600 shrink-0" />
              <span>Validity starts from today (sale date).</span>
            </div>
            <div className="p-2 rounded border bg-white flex items-center gap-1.5">
              <ShieldCheck className="size-3.5 text-amber-600 shrink-0" />
              <span>Sale is permanent & non-refundable.</span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto text-xs"
          >
            Close
          </Button>
          {onPrintReceipt && (
            <Button
              type="button"
              variant="outline"
              onClick={onPrintReceipt}
              className="w-full sm:w-auto text-xs gap-1.5 border-primary/50 text-foreground hover:bg-primary/10"
            >
              <Printer className="size-3.5 text-primary" />
              Print Receipt
            </Button>
          )}
          {onViewMemberTraining && (
            <Button
              type="button"
              variant="secondary"
              onClick={onViewMemberTraining}
              className="w-full sm:w-auto text-xs"
            >
              Member Details
            </Button>
          )}
          <Button
            type="button"
            onClick={onCreateClass}
            className="w-full sm:w-auto bg-primary text-black font-semibold hover:bg-primary/90 text-xs gap-1.5"
          >
            <Calendar className="size-3.5" />
            Generate Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};